/**
 * OperationalRow — the shared "Operational Spine" row primitive (ADR M68).
 *
 * Provides the left stripe structural chrome (a `data-slot="stripe"` div, not
 * a border) used across Tasks and Shopping List. Consumers compose their own
 * checkbox and content inside.
 *
 * Spine colors (DASH-F4 / #782: sourced from the status tokens in index.css):
 *   pending    → bg-bark-600 (neutral OPEN — no status token exists for pending)
 *   done       → --color-done
 *   overdue    → --color-overdue  + bg-overdue-row wash
 *   snoozed    → --color-snoozed + opacity-60
 *   blocked    → --color-blocked + opacity-50
 *   purchased  → --color-done (shopping list alias for "done")
 */

type OperationalRowStatus = 'pending' | 'done' | 'overdue' | 'snoozed' | 'blocked' | 'purchased';

interface OperationalRowProps {
  status: OperationalRowStatus;
  children: React.ReactNode;
  className?: string;
  /** Forwarded to the root div for test selection */
  'data-testid'?: string;
  /** When true, adds data-preview="true" to the root div */
  preview?: boolean;
}

const STRIPE_COLOR_CLASS: Record<OperationalRowStatus, string> = {
  pending:   'bg-bark-600',
  done:      'bg-[var(--color-done)]',
  overdue:   'bg-[var(--color-overdue)]',
  snoozed:   'bg-[var(--color-snoozed)]',
  blocked:   'bg-[var(--color-blocked)]',
  purchased: 'bg-[var(--color-done)]',
};

const STATUS_CLASS: Record<OperationalRowStatus, string> = {
  pending:   '',
  done:      '',
  overdue:   'bg-[var(--color-overdue-row-bg)]',
  snoozed:   'opacity-60',
  blocked:   'opacity-50',
  purchased: 'opacity-60',
};

export function OperationalRow({
  status,
  children,
  className = '',
  'data-testid': testId,
  preview,
}: OperationalRowProps) {
  return (
    <div
      className={`flex items-stretch border-b border-[var(--color-border)]/60 last:border-0 ${STATUS_CLASS[status]} transition-colors duration-100 ${className}`}
      data-status={status}
      data-testid={testId}
      {...(preview ? { 'data-preview': 'true' } : {})}
    >
      {/* Left border stripe — identifiable via data-slot="stripe" for behavioral tests (RULE-010) */}
      <div
        data-slot="stripe"
        aria-hidden="true"
        className={`w-0.5 flex-shrink-0 self-stretch ${STRIPE_COLOR_CLASS[status]}`}
      />
      {children}
    </div>
  );
}
